import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { AlertCircle, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SelectionGridSkeleton } from "./skeletons/SelectionCardSkeleton";
import { Progress } from "@/components/ui/progress";
import { TemplateSelector } from "./TemplateSelector";
import type { PersonalizationTemplate } from "@/lib/personalizationTemplates";

interface StepPersonalizationProps {
  onComplete: (data: {
    job_role: string;
    job_title?: string;
    preferred_decision_areas: string[];
    team_size?: string;
    company_phase?: string;
    decision_style?: string[];
    communication_preference?: string;
    detail_level?: string;
    current_priorities?: string[];
  }) => void;
}

const ROLES = [
  "Gründer / CEO",
  "Führungskraft / Abteilungsleitung",
  "Produkt / Strategie",
  "HR / People & Culture",
  "Individual Contributor",
  "Sonstiges",
];

const DECISION_AREAS = [
  { id: "strategy", label: "Strategie & Positionierung" },
  { id: "product", label: "Produkt & Innovation" },
  { id: "growth", label: "Wachstum & Skalierung" },
  { id: "team", label: "Team & Führung" },
  { id: "operations", label: "Operations & Prozesse" },
  { id: "finance", label: "Finanzen & Investment" },
];

const TEAM_SIZES = [
  { id: "1-10", label: "1-10 Personen", description: "Klein & agil" },
  { id: "11-50", label: "11-50 Personen", description: "Wachsend" },
  { id: "51-200", label: "51-200 Personen", description: "Skalierend" },
  { id: "200+", label: "200+ Personen", description: "Etabliert" },
];

const COMPANY_PHASES = [
  { id: "founding", label: "Gründungsphase", description: "Aufbau & Produktfindung" },
  { id: "growth", label: "Wachstumsphase", description: "First customers & PMF" },
  { id: "scaling", label: "Skalierungsphase", description: "Expansion & Strukturen" },
  { id: "established", label: "Etabliert", description: "Optimierung & Innovation" },
];

const DECISION_STYLES = [
  { id: "data-driven", label: "Datengetrieben", description: "Basierend auf Zahlen & Fakten" },
  { id: "intuitive", label: "Intuitiv", description: "Erfahrung & Bauchgefühl" },
  { id: "collaborative", label: "Kollaborativ", description: "Team-Input & Diskussion" },
  { id: "pragmatic", label: "Schnell & pragmatisch", description: "Testen & iterieren" },
];

const COMMUNICATION_PREFS = [
  { id: "formal", label: "Formell (Sie)", description: "Professionell & strukturiert" },
  { id: "informal", label: "Informell (Du)", description: "Direkt & persönlich" },
  { id: "automatic", label: "Automatisch", description: "Ich passe mich an" },
];

const DETAIL_LEVELS = [
  { id: "concise", label: "Kurz & prägnant", description: "Die wichtigsten Punkte" },
  { id: "detailed", label: "Ausführlich", description: "Tiefe Analysen & Details" },
  { id: "adaptive", label: "Adaptiv", description: "Je nach Situation" },
];

const PRIORITIES = [
  { id: "accelerate-growth", label: "Wachstum beschleunigen" },
  { id: "reduce-costs", label: "Kosten senken" },
  { id: "improve-product", label: "Produkt verbessern" },
  { id: "scale-team", label: "Team skalieren" },
  { id: "optimize-processes", label: "Prozesse optimieren" },
  { id: "find-focus", label: "Fokus finden" },
];

export const StepPersonalization = ({ onComplete }: StepPersonalizationProps) => {
  const [showTemplateSelector, setShowTemplateSelector] = useState(true);
  const [currentSubStep, setCurrentSubStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [teamSize, setTeamSize] = useState<string | null>(null);
  const [companyPhase, setCompanyPhase] = useState<string | null>(null);
  const [decisionStyles, setDecisionStyles] = useState<string[]>([]);
  const [communicationPref, setCommunicationPref] = useState<string>("automatic");
  const [detailLevel, setDetailLevel] = useState<string>("adaptive");
  const [priorities, setPriorities] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 150);
    return () => clearTimeout(timer);
  }, []);

  const handleTemplateSelect = (template: PersonalizationTemplate) => {
    // Apply template data
    if (template.data.job_role) setSelectedRole(template.data.job_role);
    if (template.data.preferred_decision_areas) setSelectedAreas(template.data.preferred_decision_areas);
    if (template.data.team_size) setTeamSize(template.data.team_size);
    if (template.data.company_phase) setCompanyPhase(template.data.company_phase);
    if (template.data.decision_style) setDecisionStyles(template.data.decision_style);
    if (template.data.communication_preference) setCommunicationPref(template.data.communication_preference);
    if (template.data.detail_level) setDetailLevel(template.data.detail_level);
    if (template.data.current_priorities) setPriorities(template.data.current_priorities);
    
    setShowTemplateSelector(false);
  };

  const handleSkipTemplate = () => {
    setShowTemplateSelector(false);
  };

  const toggleArea = (areaId: string) => {
    setSelectedAreas(prev =>
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const toggleDecisionStyle = (styleId: string) => {
    setDecisionStyles(prev =>
      prev.includes(styleId)
        ? prev.filter(id => id !== styleId)
        : [...prev, styleId]
    );
  };

  const togglePriority = (priorityId: string) => {
    setPriorities(prev =>
      prev.includes(priorityId)
        ? prev.filter(id => id !== priorityId)
        : [...prev, priorityId]
    );
  };

  const handleNext = () => {
    setValidationError("");

    if (currentSubStep === 1 && !selectedRole) {
      setValidationError("Bitte wähle deine Rolle aus");
      return;
    }

    if (currentSubStep < 3) {
      setCurrentSubStep(currentSubStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentSubStep > 1) {
      setCurrentSubStep(currentSubStep - 1);
      setValidationError("");
    }
  };

  const handleSubmit = () => {
    onComplete({
      job_role: selectedRole!,
      job_title: title || undefined,
      preferred_decision_areas: selectedAreas,
      team_size: teamSize || undefined,
      company_phase: companyPhase || undefined,
      decision_style: decisionStyles.length > 0 ? decisionStyles : undefined,
      communication_preference: communicationPref,
      detail_level: detailLevel,
      current_priorities: priorities.length > 0 ? priorities : undefined,
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (currentSubStep === 1 && selectedRole) {
          handleNext();
        } else if (currentSubStep > 1) {
          handleNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSubStep, selectedRole]);

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-2xl md:text-[40px] leading-[1.1] tracking-tight font-bold font-serif text-foreground">
            Personalisiere deinen Denkraum
          </h1>
          <p className="text-muted-foreground mt-2">
            Diese Angaben bestimmen, wie Quentari deine Entscheidungen begleitet.
          </p>
        </div>
        <SelectionGridSkeleton count={6} columns={2} />
      </div>
    );
  }

  // Show template selector first
  if (showTemplateSelector) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-2xl md:text-[40px] leading-[1.1] tracking-tight font-bold font-serif text-foreground">
            Personalisiere deinen Denkraum
          </h1>
          <p className="text-muted-foreground mt-2">
            Starte mit einem Template oder fülle alles selbst aus.
          </p>
        </div>
        <TemplateSelector 
          onSelectTemplate={handleTemplateSelect}
          onSkip={handleSkipTemplate}
        />
      </div>
    );
  }

  const progressValue = (currentSubStep / 3) * 100;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-sm font-medium text-muted-foreground">
            Schritt {currentSubStep} von 3
          </span>
          <Progress value={progressValue} className="flex-1 h-2" />
        </div>
        <h1 className="text-2xl md:text-[40px] leading-[1.1] tracking-tight font-bold font-serif text-foreground">
          {currentSubStep === 1 && "Deine Rolle"}
          {currentSubStep === 2 && "Dein Kontext"}
          {currentSubStep === 3 && "Deine Präferenzen"}
        </h1>
        <p className="text-muted-foreground mt-2">
          {currentSubStep === 1 && "Lass uns mit deiner Position und deinen Schwerpunkten starten."}
          {currentSubStep === 2 && "Erzähl mir mehr über dein Team und Unternehmen."}
          {currentSubStep === 3 && "Wie soll ich mit dir kommunizieren und Entscheidungen angehen?"}
        </p>
      </div>

      {/* Step 1: Role & Decision Areas */}
      {currentSubStep === 1 && (
        <>
          <div className="space-y-4">
            <Label className="text-base font-semibold">Deine Rolle im Unternehmen *</Label>
            <p className="text-sm text-muted-foreground -mt-2">
              Deine Rolle beeinflusst, welche strategischen Aspekte ich priorisiere.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {ROLES.map((role) => (
                <button
                  key={role}
                  onClick={() => setSelectedRole(role)}
                  className={cn(
                    "flex items-center justify-center rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02] min-h-[64px]",
                    selectedRole === role
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-base leading-tight", selectedRole === role ? "text-foreground" : "text-foreground/90")}>
                    {role}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {selectedRole && (
            <div className="space-y-3 animate-fade-in">
              <Label htmlFor="title" className="text-base">Deine Berufsbezeichnung (optional)</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z.B. Head of Product"
                className="h-12 text-base"
                maxLength={100}
              />
            </div>
          )}

          {selectedRole && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <Label className="text-base font-semibold">Bevorzugte Entscheidungsbereiche (optional)</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Je nach deinem Fokus passe ich meine Fragen und Analysen an.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {DECISION_AREAS.map((area) => (
                  <button
                    key={area.id}
                    onClick={() => toggleArea(area.id)}
                    className={cn(
                      "flex items-center justify-between rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02] min-h-[64px]",
                      selectedAreas.includes(area.id)
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <p className={cn("font-semibold text-base leading-tight", selectedAreas.includes(area.id) ? "text-foreground" : "text-foreground/90")}>
                      {area.label}
                    </p>
                    {selectedAreas.includes(area.id) && (
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Step 2: Team Context */}
      {currentSubStep === 2 && (
        <>
          <div className="space-y-4">
            <Label className="text-base font-semibold">Teamgröße (optional)</Label>
            <p className="text-sm text-muted-foreground -mt-2">
              Die Teamgröße hilft mir, passende Skalierungs-Strategien vorzuschlagen.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {TEAM_SIZES.map((size) => (
                <button
                  key={size.id}
                  onClick={() => setTeamSize(size.id)}
                  className={cn(
                    "flex flex-col items-start rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02] min-h-[80px]",
                    teamSize === size.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-base", teamSize === size.id ? "text-foreground" : "text-foreground/90")}>
                    {size.label}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{size.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-base font-semibold">Unternehmensphase (optional)</Label>
            <p className="text-sm text-muted-foreground -mt-2">
              Die Phase bestimmt, welche Herausforderungen für dich relevant sind.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {COMPANY_PHASES.map((phase) => (
                <button
                  key={phase.id}
                  onClick={() => setCompanyPhase(phase.id)}
                  className={cn(
                    "flex flex-col items-start rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02] min-h-[80px]",
                    companyPhase === phase.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-base", companyPhase === phase.id ? "text-foreground" : "text-foreground/90")}>
                    {phase.label}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{phase.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Aktuelle Prioritäten (optional)</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Was sind deine wichtigsten Herausforderungen gerade?
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PRIORITIES.map((priority) => (
                <button
                  key={priority.id}
                  onClick={() => togglePriority(priority.id)}
                  className={cn(
                    "flex items-center justify-between rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02] min-h-[56px]",
                    priorities.includes(priority.id)
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-sm leading-tight", priorities.includes(priority.id) ? "text-foreground" : "text-foreground/90")}>
                    {priority.label}
                  </p>
                  {priorities.includes(priority.id) && (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Step 3: Decision Style & Communication */}
      {currentSubStep === 3 && (
        <>
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Entscheidungsstil (optional)</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Wie triffst du am liebsten Entscheidungen? (Mehrfachauswahl möglich)
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {DECISION_STYLES.map((style) => (
                <button
                  key={style.id}
                  onClick={() => toggleDecisionStyle(style.id)}
                  className={cn(
                    "flex flex-col items-start rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02] min-h-[80px] relative",
                    decisionStyles.includes(style.id)
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-base", decisionStyles.includes(style.id) ? "text-foreground" : "text-foreground/90")}>
                    {style.label}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{style.description}</p>
                  {decisionStyles.includes(style.id) && (
                    <CheckCircle2 className="h-5 w-5 text-primary absolute top-3 right-3" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-base font-semibold">Kommunikationsstil</Label>
            <p className="text-sm text-muted-foreground -mt-2">
              Wie soll ich dich ansprechen?
            </p>
            <div className="grid grid-cols-1 gap-3">
              {COMMUNICATION_PREFS.map((pref) => (
                <button
                  key={pref.id}
                  onClick={() => setCommunicationPref(pref.id)}
                  className={cn(
                    "flex flex-col items-start rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02]",
                    communicationPref === pref.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-base", communicationPref === pref.id ? "text-foreground" : "text-foreground/90")}>
                    {pref.label}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{pref.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-base font-semibold">Detailtiefe</Label>
            <p className="text-sm text-muted-foreground -mt-2">
              Wie ausführlich sollen meine Analysen sein?
            </p>
            <div className="grid grid-cols-1 gap-3">
              {DETAIL_LEVELS.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setDetailLevel(level.id)}
                  className={cn(
                    "flex flex-col items-start rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02]",
                    detailLevel === level.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-base", detailLevel === level.id ? "text-foreground" : "text-foreground/90")}>
                    {level.label}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{level.description}</p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {validationError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-3 justify-between pt-4">
        <Button
          onClick={handleBack}
          variant="outline"
          disabled={currentSubStep === 1}
          className="h-12 px-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück
        </Button>
        <Button
          onClick={handleNext}
          disabled={currentSubStep === 1 && !selectedRole}
          className="h-12 px-8 text-base font-semibold"
        >
          {currentSubStep === 3 ? "Fertig" : "Weiter"}
          {currentSubStep < 3 && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};